// API client for frontend
export interface Piece {
  id: number;
  title: string;
  description: string | null;
  status: string;
  orderIndex: number;
  materials: string | null;
  dimensions: string | null;
  year: string | null;
  createdAt: string;
  updatedAt: string;
  images: PieceImage[];
}

export interface PieceImage {
  id: number;
  pieceId: number;
  imagePath: string;
  thumbnailPath: string;
  imageOrder: number;
  createdAt: string;
}

export async function fetchPieces(): Promise<Piece[]> {
  const res = await fetch("/api/pieces");
  if (!res.ok) throw new Error("Failed to fetch pieces");
  return res.json();
}

export async function fetchPiece(id: number): Promise<Piece> {
  const res = await fetch(`/api/pieces/${id}`);
  if (!res.ok) throw new Error("Failed to fetch piece");
  return res.json();
}

export async function createPiece(
  data: Partial<Piece>,
  pin: string
): Promise<Piece> {
  const res = await fetch("/api/pieces", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create piece");
  return res.json();
}

export async function updatePiece(
  id: number,
  data: Partial<Piece>,
  pin: string
): Promise<Piece> {
  const res = await fetch(`/api/pieces/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update piece");
  return res.json();
}

export async function deletePiece(id: number, pin: string): Promise<void> {
  const res = await fetch(`/api/pieces/${id}`, {
    method: "DELETE",
    headers: {
      "X-Admin-Pin": pin,
    },
  });
  if (!res.ok) throw new Error("Failed to delete piece");
}

export async function reorderPieces(
  pieceIds: number[],
  pin: string
): Promise<void> {
  const res = await fetch("/api/pieces/reorder", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify({ pieceIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder pieces");
}

export async function uploadImages(
  pieceId: number,
  files: File[],
  pin: string
): Promise<{ images: PieceImage[] }> {
  const formData = new FormData();
  formData.append("pieceId", pieceId.toString());

  files.forEach((file, index) => {
    formData.append(`image${index}`, file);
  });

  const res = await fetch("/api/upload", {
    method: "POST",
    headers: {
      "X-Admin-Pin": pin,
    },
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to upload images");
  return res.json();
}

// ============================================
// Exhibit Types and Functions
// ============================================

export type AspectRatio = "square" | "tall" | "wide";

export interface ExhibitImage {
  id: number;
  exhibitId: number;
  imagePath: string;
  thumbnailPath: string;
  aspectRatio: AspectRatio;
  width: number;
  height: number;
  title: string | null;
  caption: string | null;
  imageOrder: number;
  createdAt: string;
}

export type HeaderVariant =
  | "default"
  | "expanding-lines"
  | "growing-circle"
  | "organic-strokes"
  | "minimal-lines"
  | "elegant-frame";

export interface Exhibit {
  id: number;
  title: string | null;
  description: string | null;
  headerVariant: HeaderVariant;
  visible: boolean;
  isGallery: boolean;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  images: ExhibitImage[];
}

// Public: fetch visible exhibits
export async function fetchExhibits(): Promise<Exhibit[]> {
  const res = await fetch("/api/exhibits");
  if (!res.ok) throw new Error("Failed to fetch exhibits");
  return res.json();
}

// Admin: fetch all exhibits including hidden
export async function fetchAllExhibits(pin: string): Promise<Exhibit[]> {
  const res = await fetch("/api/exhibits/all", {
    headers: { "X-Admin-Pin": pin },
  });
  if (res.status === 401) throw new AuthError("Unauthorized");
  if (!res.ok) throw new Error("Failed to fetch exhibits");
  return res.json();
}

// Admin: verify PIN is valid
export async function verifyPin(pin: string): Promise<boolean> {
  const res = await fetch("/api/exhibits/all", {
    headers: { "X-Admin-Pin": pin },
  });
  return res.ok;
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

// Fetch single exhibit
export async function fetchExhibit(id: number): Promise<Exhibit> {
  const res = await fetch(`/api/exhibits/${id}`);
  if (!res.ok) throw new Error("Failed to fetch exhibit");
  return res.json();
}

// Admin: create exhibit
export async function createExhibit(
  data: Partial<Exhibit>,
  pin: string
): Promise<Exhibit> {
  const res = await fetch("/api/exhibits", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create exhibit");
  return res.json();
}

// Admin: update exhibit
export async function updateExhibit(
  id: number,
  data: Partial<Exhibit>,
  pin: string
): Promise<Exhibit> {
  const res = await fetch(`/api/exhibits/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update exhibit");
  return res.json();
}

// Admin: delete exhibit
export async function deleteExhibit(id: number, pin: string): Promise<void> {
  const res = await fetch(`/api/exhibits/${id}`, {
    method: "DELETE",
    headers: { "X-Admin-Pin": pin },
  });
  if (!res.ok) throw new Error("Failed to delete exhibit");
}

// Admin: reorder exhibits
export async function reorderExhibits(
  exhibitIds: number[],
  pin: string
): Promise<void> {
  const res = await fetch("/api/exhibits/reorder", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify({ exhibitIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder exhibits");
}

// Admin: upload images to exhibit
export async function uploadExhibitImages(
  exhibitId: number,
  files: File[],
  pin: string
): Promise<{ images: ExhibitImage[] }> {
  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append(`image${index}`, file);
  });

  const res = await fetch(`/api/exhibits/${exhibitId}/images`, {
    method: "POST",
    headers: { "X-Admin-Pin": pin },
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to upload images");
  return res.json();
}

// Admin: delete image from exhibit
export async function deleteExhibitImage(
  exhibitId: number,
  imageId: number,
  pin: string
): Promise<void> {
  const res = await fetch(`/api/exhibits/${exhibitId}/images/${imageId}`, {
    method: "DELETE",
    headers: { "X-Admin-Pin": pin },
  });
  if (!res.ok) throw new Error("Failed to delete image");
}

// Admin: reorder images within exhibit
export async function reorderExhibitImages(
  exhibitId: number,
  imageIds: number[],
  pin: string
): Promise<void> {
  const res = await fetch(`/api/exhibits/${exhibitId}/images/reorder`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify({ imageIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder images");
}

// Admin: update image title/caption
export async function updateExhibitImage(
  exhibitId: number,
  imageId: number,
  data: { title?: string | null; caption?: string | null },
  pin: string
): Promise<ExhibitImage> {
  const res = await fetch(`/api/exhibits/${exhibitId}/images/${imageId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update image");
  return res.json();
}
